import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
} from '@angular/forms';
import { SelectOption } from '@form';
import { TranslocoService } from '@jsverse/transloco';
import {
  FilterConfig,
  FilterFormGroup,
  FilterFormValue,
} from '../models/filters.models';
import { distinctUntilChanged, Observable, Subject, takeUntil } from 'rxjs';
import { generateUniqueId } from '@ui';

@Injectable({ providedIn: 'root' })
export class FiltersService implements OnDestroy {
  private fb = inject(FormBuilder);
  private translocoService = inject(TranslocoService);
  private destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();  // Emite un valor para completar los observables
    this.destroy$.complete();  // Completa el subject
  }

  public createFilterFormGroup(filter?:FilterConfig): FormGroup {
    return this.fb.group<FilterFormGroup>({
      id: generateUniqueId(),
      field: '',
      label: '',
      type: null,
      condition: filter?.condition || 'equals',
      value: filter?.value || null,
    });
  }

  public getLocalizedOptions(options: any[] | undefined): any[] {
    return (
      options?.map((option) => ({
        ...option,
        label: this.translocoService.translate(option.label),
      })) || []
    );
  }

  public localizeNumberFilterOperators(
    options: SelectOption[],
  ): Observable<SelectOption[]> {
    return new Observable((observer) => {
      this.translocoService
        .selectTranslation()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          const localized = options.map((option) => ({
            ...option,
            label: this.translocoService.translate(option.label),
          }));
          observer.next(localized);
          observer.complete();
        });
    });
  }

  public localizeTypesFilterCategory(
    options: any,
  ): Observable<any[]> {
    return new Observable((observer) => {
      this.translocoService
        .selectTranslation()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          const localized = options.map((option:any) => ({
            ...option,
            label: this.translocoService.translate(option.label),
            options: option?.options?.map((optGroup:any)=>({
                ...optGroup,
                label: this.translocoService.translate(optGroup.label),
                options: this.getLocalizedOptions(optGroup.options),
            }))
          }));
          observer.next(localized);
          observer.complete();
        });
    });
  }

  public localizeDefaultFilters(filters: FilterConfig[]): Observable<FilterConfig[]> {
    return new Observable((observer) => {
      this.translocoService
        .selectTranslation()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          const localized = filters.map((filter) => ({
            ...filter,
            label: this.translocoService.translate(filter.label),
            placeholder: filter.placeholder
              ? this.translocoService.translate(filter.placeholder)
              : undefined,
          }));
          observer.next(localized);
          observer.complete();
        });
    });
  }

  public resetValueControl(group: AbstractControl, valueKey: string): void {
    group.get(valueKey)?.reset();
  }

  public updateFilterGroup(filter: FilterConfig, group: AbstractControl): void {
    const props = ['type', 'label', 'condition'];
    props.forEach((prop) => {
      const propCtrl = group.get(prop);
      propCtrl?.patchValue((filter as any)[prop], { emitEvent: false });
    });
  }

  public buildFiltersArrayValueChanges(
    filtersArray: FormArray,
    defaultFilters: FilterConfig[],
    filtersSignal: (filters: FilterConfig[]) => void,
  ): void {
    filtersArray.valueChanges
      .pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged())
      .subscribe((filters: FilterConfig[]) => {
        const filtersApplied = filters.map((filter) => filter.field);
        const updatedFilters = defaultFilters.map((filter) => ({
          ...filter,
          disabled: filtersApplied.includes(filter.value),
        }));
        filtersSignal(updatedFilters);
      });
  }

  public mapFormValues(filters: FilterFormGroup[]): FilterFormValue[] {
    return filters
      .map((filter) => ({
        name: filter.field,
        operator: filter.condition,
        value: filter.value,
      }))
      .filter((item) => item.name !== null && item.value !== null);
  }
}
